// react components
import { useState, useEffect, useCallback, useMemo } from "react";
//api
import api from "api/api";

//redux
import { useSelector, useDispatch } from "react-redux";
import { loadSwitchState, loadProductListState } from "store/features/respondentDashboardSlice";

// react-router-dom components
import { useNavigate, useLocation } from "react-router-dom";

// mui components

// mui-icons components
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

//routes

//assets

//components
import FiltersHeaderLabel from "../components/common/FiltersHeaderLabel";
import DashboardFilters from "../components/respondentDashboard/DashboardFilters";
import Dashboard from "../components/respondentDashboard/Dashboard";
import Loader from "../components/common/Loader";

// layout
import SideNavLayout from "../components/respondentDashboard/layout/SideNavLayout";
import Header from "../components/common/Header";

//utils
import { checkFilterSelection, consumerFilterPayload } from "../utils/consumerFilterUtils";
import { showNotification } from "store/features/notificationSlice";

function RespondentDashboard({ headerTitle }) {
    const height = 130;
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const consumerFilterStoreData = useSelector((state) => state.consumerFilterStore.currentSelectedFilters);
    const selectedFilters = useSelector((state) => state.respondentDashboardStore.currentFilter) || {};
    const selectedProducts = useSelector((state) => state.respondentDashboardStore.productList);

    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    const [dashboardData, setDashboardData] = useState([]);
    const [overallData, setOverallData] = useState({});
    const [filterData, setFilterData] = useState([]);
    const [settings, setSettings] = useState([]);

    const [additionalProductPerformanceData, setAdditionalProductPerformanceData] = useState([]);

    // loader
    const [isLoading, setIsLoading] = useState(false);

    const parseProductString = (option = "") => {
        let productTitle;
        let cd;

        const spilitArr = option.split("*/*");
        if (spilitArr.length > 1) {
            cd = spilitArr[spilitArr.length - 1];
            productTitle = spilitArr[0];
        }

        return {
            pc: productTitle,
            cd: cd,
        };
    };

    const joinArraysInObject = useCallback(() => {
        const obj = selectedFilters;
        const result = {};
        for (const key in obj) {
            if (Array.isArray(obj[key])) {
                if (key === "product_codes") {
                    result["product_code"] = obj[key].map((product) => {
                        const { pc, cd } = parseProductString(product);
                        return {
                            pc: pc,
                            core_data_model_id: cd,
                        };
                    });
                } else {
                    result[key] = obj[key].map((item) => item.replace(/,/g, " |")).join(",");
                }
            }
        }
        return result;
    }, [selectedFilters]);

    const reportMetaData = () => {
        if (selectedProducts.length !== 0) {
            // TTA card-level filters passed via navigation state (only present when coming from TTA page)
            const textThemeFilters = location.state?.textThemeFilters || {};
            const productDetails = selectedProducts?.map((datum) => {
                let data = {
                    pc: datum.productName.split("_").join(" "),
                    core_data_model_id: datum.coreDataId.toString(),
                    is_competitor: datum.isCompetitor,
                };
                // Use TTA card filters for this product if available, else fall back to global consumer filter store
                const ttaKey = `${datum.productName}__${datum.coreDataId}`;
                const _tempFilters = Object.keys(textThemeFilters).length > 0
                    ? (textThemeFilters[ttaKey] || {})
                    : consumerFilterPayload(consumerFilterStoreData, datum.productName, datum.coreDataId);
                if (_tempFilters && Object.keys(_tempFilters).length > 0) {
                    data = { ...data, ..._tempFilters };
                }
                return data;
            });
            return [...productDetails];
        }
    };

    const memoizedFilters = useMemo(() => {
        const joinedData = joinArraysInObject();
        return joinedData || {};
    }, [joinArraysInObject]);

    const getFilteredIds = (productSequences) => {
        let coreDataIdsForPayload;
        if (checkFilterSelection(consumerFilterStoreData)) {
            const ids = productSequences.map((datum) => datum.core_data_model_id);
            coreDataIdsForPayload = [...new Set(ids)];
        } else {
            coreDataIdsForPayload = selectedStudy.map((ele) => ele.id);
        }
        
        const obj = {
            cdIds: coreDataIdsForPayload,
            study_type: selectedStudy?.[0]?.study_type,
            study_type_method: selectedStudy?.[0]?.study_type_method,
        };
        return obj;
    };

    const fetchRespondentData = useCallback(async () => {
        const productSequences = reportMetaData();
        const obj = getFilteredIds(productSequences);
        const filteredCoreDataIds = obj["cdIds"];
        const study_type = obj["study_type"];
        const study_type_method = obj["study_type_method"];

        const url = process.env.REACT_APP_SERVER_PATH + `/study-datas/respondent-profile-dashboard/`;

        const payload = {
            core_data_ids: filteredCoreDataIds.join(","),
            product_category: productCategoryId,
            study_type: study_type,
            study_type_method: study_type_method,
            filters: memoizedFilters,
            product_sequences: productSequences,
        };

        try {
            setIsLoading(true);
            await api
                .post(url, payload) // Use memoizedFilters here
                .then((res) => {
                    setDashboardData(res.data.computed_data);
                    setOverallData(res.data.overall_data);
                    setAdditionalProductPerformanceData(res.data.add_pro_per_data);
                    if (dashboardData.length === 0) {
                        let tempObj = {};
                        res.data.computed_data.forEach((ele) => {
                            tempObj[ele.backend_variable] = "countGraph";
                        });
                        dispatch(loadSwitchState({ ...tempObj }));
                    }
                    setIsLoading(false);
                })
                .catch(async (error) => {
                    handleError("error", error);
                    setIsLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsLoading(false);
        }
    }, [selectedStudy, dispatch, productCategoryId, memoizedFilters]);

    const fetchRespondentFilterData = useCallback(async () => {
        const productSequences = reportMetaData();
        const obj = getFilteredIds(productSequences);
        const filteredCoreDataIds = obj["cdIds"];
        const study_type = obj["study_type"];
        const study_type_method = obj["study_type_method"];
        const url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/dynamic-respondent-filters/`;

        const payload = {
            core_data_ids: filteredCoreDataIds.join(","),
            product_category: productCategoryId,
            product_sequences: productSequences,
            study_type: study_type,
            study_type_method: study_type_method,
        };

        try {
            await api
                .post(url, payload)
                .then((res) => {
                    setFilterData(res.data);
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [selectedStudy]);

    const fetchSettingsData = useCallback(() => {
        let url = process.env.REACT_APP_SERVER_PATH + `/respondent-settings/all/?archive=False`;

        if (productCategoryId) {
            url += `&product_category=${productCategoryId}`;
        }

        try {
            api.get(url)
                .then(async (res) => {
                    setSettings([...res.data]);
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [productCategoryId]);

    useEffect(() => {
        fetchSettingsData();
    }, [fetchSettingsData]);

    useEffect(() => {
        if (selectedStudy.length === 0) {
            navigate("/studies");
        } else {
            fetchRespondentData();
        }
    }, [selectedStudy, fetchRespondentData]);

    useEffect(() => {
        if (selectedStudy.length === 0) {
            navigate("/studies");
        } else {
            fetchRespondentFilterData();
        }
    }, [selectedStudy, fetchRespondentFilterData]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const handleBack = () => {
        dispatch(loadProductListState([]));
        navigate(-1);
    };

    return (
        <>
            {isLoading && <Loader />}
            <Header
                title={headerTitle}
                isStudySelected={false}
            />
            <div
                className="ms-4 me-4 mb-0 mt-3 background-color-white box-shadow--2 d-flex"
                style={{
                    borderRadius: "5px",
                }}
            >
                <FiltersHeaderLabel multipleLabel={true} />
            </div>
            <div
                style={{
                    minHeight: "calc(100vh - 130px)",
                    display: "flex",
                    background: "#eee",
                    padding: "10px",
                }}
                className="full-container m-4 background-color-white  box-shadow--full-container"
            >
                <div
                    className=""
                    style={{
                        width: 150,
                        display: "flex",
                        alignItems: "center",
                        position: "absolute",
                        zIndex: 10,
                        margin: "-40px -30px",
                    }}
                >
                    <button
                        className="p-2 liner-light-blue-background color-white"
                        style={{
                            borderRadius: "50%",
                            position: "relative",
                            top: 5,
                            left: 0,
                            cursor: "pointer",
                            border: "none",
                        }}
                        onClick={handleBack}
                    >
                        <span>
                            <ArrowBackIcon className="color-white" />
                        </span>
                    </button>
                </div>
                <SideNavLayout height={height}>
                    <DashboardFilters
                        height={height}
                        filterData={filterData}
                        settings={settings}
                    />
                </SideNavLayout>
                <div
                    style={{
                        paddingLeft: "20px",
                        maxHeight: `calc(100vh - ${height}px)`,
                        overflowY: "auto",
                        width: "100%",
                    }}
                >
                    <Dashboard
                        filterData={filterData}
                        data={dashboardData}
                        overallData={overallData}
                        additionalProductPerformanceData={additionalProductPerformanceData}
                        settings={settings}
                        fetchSettingsData={fetchSettingsData}
                        navigateFrom={location?.state?.from}
                        textThemeFilters={location?.state?.textThemeFilters || {}}
                    />
                </div>
            </div>
        </>
    );
}

// Typechecking props for the RespondentDashboard
RespondentDashboard.propTypes = {
    headerTitle: PropTypes.string,
};

export default RespondentDashboard;
